import React from "react";
import { connect } from "react-redux";
import { InjectedIntl, injectIntl } from "@ai360/core/intl";
import { DialogBox, DialogBoxFooterType } from "~/core";
import * as analysisActions from "../analysis-info/actions";
import { messages } from "../i18n-messages";
import { LayerAPI } from "@ai360/core";
import { selectors as cdSelectors } from "~/customer-data";
import * as models from "~/customer-data/models";

interface IAnalysisBatchDeleteDialogBoxProps {
    closeConfirmDialog: () => void;
    fieldMap: Immutable.Map<string, models.FieldInfo>;
    intl: InjectedIntl;
    isConfirmationModalOpen: boolean;
    onDeleteSingle: () => void;
    onDeleteAnalysisLayerBatch: (
        batchGuid: string,
        analysisInBatch: LayerAPI.IAnalysisInBatch[]
    ) => void;
    surfaceInfo: LayerAPI.ISubLayer;
}

const AnalysisBatchDeleteDialogBox = (props: IAnalysisBatchDeleteDialogBoxProps): JSX.Element => {
    const {
        closeConfirmDialog,
        fieldMap,
        isConfirmationModalOpen,
        intl,
        onDeleteSingle,
        onDeleteAnalysisLayerBatch,
        surfaceInfo,
    } = props;
    const { formatMessage } = intl;

    const body = formatMessage(messages.deleteMultipleBody, {
        analysisLayerName: surfaceInfo.analysisLayerName,
        fieldName: surfaceInfo.fieldName,
    });
    const farmFieldNames = surfaceInfo.analysisInBatch.map((x) => {
        const farmName = fieldMap.get(x.fieldGuid)?.farmName
            ? `${fieldMap.get(x.fieldGuid)?.farmName} - `
            : "";
        return `${farmName}${x.fieldName}`;
    });
    const sortedFieldNames = farmFieldNames.sort((a, b) => a.localeCompare(b));
    const fieldNameElements = sortedFieldNames.map((x, i) => (
        <li className="list-item" key={i}>
            {x}
        </li>
    ));

    const onDeleteAll = () => {
        onDeleteAnalysisLayerBatch(surfaceInfo.batchGuid, surfaceInfo.analysisInBatch);
        closeConfirmDialog();
    };

    return (
        <DialogBox
            className="surface-layer-context-menu-batch-dialog-box"
            footerType={DialogBoxFooterType.MULTI_ACTION}
            isOpen={isConfirmationModalOpen}
            multiActionList={[
                {
                    action: formatMessage(messages.deleteAllButton),
                    actionDisabled: false,
                    onAction: () => onDeleteAll(),
                },
                {
                    action: formatMessage(messages.deleteSingleButton),
                    actionDisabled: false,
                    onAction: () => onDeleteSingle(),
                },
                {
                    action: formatMessage(messages.cancel),
                    actionDisabled: false,
                    isCancelStyle: true,
                    onAction: () => closeConfirmDialog(),
                },
            ]}
            title={formatMessage(messages.confirmDeleteTitle)}
            testId="analysis-batch-delete-confirm-modal"
        >
            <div className="body">{body}</div>
            <ul className="list">{fieldNameElements}</ul>
        </DialogBox>
    );
};
const mapStateToProps = (state, props: Partial<IAnalysisBatchDeleteDialogBoxProps>) => ({
    ...props,
    fieldMap: cdSelectors.getFieldMap(state),
});

const mapDispatchToProps = (dispatch) => ({
    onShowAnalysisLayer: (analysisInfoGuid) =>
        dispatch(analysisActions.showAnalysisInfo(analysisInfoGuid)),
    onDeleteAnalysisLayer: (analysisLayerGuid, fieldGuid, layerType) =>
        dispatch(analysisActions.deleteAnalysisLayer(analysisLayerGuid, fieldGuid, layerType)),
    onDeleteAnalysisLayerBatch: (batchGuid, analysisInBatch) =>
        dispatch(analysisActions.deleteAnalysisLayerBatch(batchGuid, analysisInBatch)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(AnalysisBatchDeleteDialogBox));
